/**
 * Fetches all entries from the Shop Location metaobject with all their fields
 * @returns {Promise<Array<any>>} Array of all metaobject entries with processed fields
 */
async function fetchAllShopLocations() {
    const token = "ec8b68546ca482f997b7c781423947ee";
    const url = "/api/2024-10/graphql.json";
  
    const query = `
      query getMetaobjects($cursor: String) {
        metaobjects(type: "shop_location", first: 100, after: $cursor) {
          edges {
            cursor
            node {
              id
              handle
              type
              fields {
                key
                value
                type
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                      width
                      height
                    }
                  }
                }
                references(first: 100) {
                  edges {
                    node {
                      ... on MediaImage {
                        image {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              updatedAt
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;
  
    /** @type {Array<any>} */
    const allEntries = [];
    /** @type {string|null} */
    let cursor = null;
    let pageCount = 0;
    const MAX_PAGES = 10; // Safety limit: 10 pages = 1000 entries max
  
    while (true) {
      pageCount++;
  
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({ query, variables: { cursor } }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const result = await response.json();
  
        // Check for GraphQL errors
        if (result.errors) {
          console.error("GraphQL Errors:", result.errors);
          throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
        }
  
        if (!result.data?.metaobjects) {
          console.error("Unexpected response structure:", result);
          throw new Error("Invalid response structure");
        }
  
        const { edges, pageInfo } = result.data.metaobjects;
  
        // Process each entry and extract all fields
        const processedEntries = edges.map((/** @type {any} */ edge) => {
          const node = edge.node;
          
          /** @type {any} */
          const entry = {
            id: node.id,
            handle: node.handle,
            type: node.type,
            updatedAt: node.updatedAt,
            fields: {}
          };
  
          // Process all fields from the entry
          if (Array.isArray(node.fields)) {
            node.fields.forEach((/** @type {any} */ field) => {
              entry.fields[field.key] = processField(field);
            });
          }
  
          return entry;
        });
  
        allEntries.push(...processedEntries);
  
        // Check if there are more pages
        if (!pageInfo.hasNextPage) {
          break;
        }
  
        cursor = pageInfo.endCursor;
        
        // Safety check to prevent infinite loops
        if (pageCount >= MAX_PAGES) {
          console.warn(`Reached maximum page limit (${MAX_PAGES} pages = ${MAX_PAGES * 100} entries). Stopping.`);
          break;
        }
      } catch (error) {
        console.error("Error fetching shop locations:", error);
        throw error;
      }
    }
    return allEntries;
  }
  
  /**
   * Processes a single field and extracts its value based on its type
   * @param {any} field - The field object from GraphQL response
   * @returns {any} Processed field object with value and type
   */
  function processField(field) {
    /** @type {any} */
    const processedField = {
      value: field.value,
      type: field.type
    };
  
    // Handle single reference (e.g., MediaImage)
    if (field.reference) {
      if (field.reference.image) {
        // MediaImage reference
        processedField.value = {
          url: field.reference.image.url,
          altText: field.reference.image.altText || null,
          width: field.reference.image.width || null,
          height: field.reference.image.height || null
        };
      } else {
        // Other reference types
        processedField.value = field.reference;
      }
    }
    
    // Handle references array (multiple references)
    if (field.references?.edges?.length > 0) {
      processedField.value = field.references.edges.map((/** @type {any} */ refEdge) => {
        const refNode = refEdge.node;
        
        if (refNode?.image) {
          // MediaImage in references array
          return {
            url: refNode.image.url,
            altText: refNode.image.altText || null,
            width: refNode.image.width || null,
            height: refNode.image.height || null
          };
        }
        
        // Other reference types in array
        return refNode;
      });
    }
  
    return processedField;
  }
  
  